import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import AnnouncementsSection from "../components/announcements/AnnouncementsSection";
import ContactMessagesList from "../components/trial-requests/ContactMessagesList";
import DeletionRequestsList from "../components/trial-requests/DeletionRequestsList";
import TrialRequestsList from "../components/trial-requests/TrialRequestsList";
import { TrialRequestStatus } from "../features/trial-requests/contracts/TrialRequestResponse";
import { selectBookingPageSettings } from "../features/trial-requests/trial-requests-slice";
import {
  fetchBookingPageSettings,
  fetchTrialRequests,
} from "../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { ROUTES } from "../routes";

const FILTERS: Array<TrialRequestStatus | "All"> = ["All", "Pending", "Confirmed", "Declined"];

const tabClass = (active: boolean) =>
  active
    ? "rounded-full bg-primary-40 px-4 py-1.5 text-sm font-medium text-white dark:bg-primary-90 dark:text-primary-10"
    : "rounded-full bg-grey-95 px-4 py-1.5 text-sm font-medium text-grey-20 hover:bg-grey-90 dark:bg-primary-20 dark:text-white dark:hover:bg-[#3E334D]";

/**
 * The inbox: trial requests + contact messages. The public page itself is
 * managed on the Website page — a hint points there while it's disabled.
 */
const TrialRequestsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const [filter, setFilter] = useState<TrialRequestStatus | "All">("All");

  useEffect(() => {
    dispatch(fetchBookingPageSettings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTrialRequests({ status: filter === "All" ? undefined : filter }));
  }, [dispatch, filter]);

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full min-w-0 p-4 pb-10 lg:px-10">
          <h1 className="mb-4 text-2xl font-bold text-grey-20 dark:text-white">
            {t("trialRequests.title", "Inbox")}
          </h1>

          {settings !== null && !settings.enabled && (
            <div className="with-border mb-6 rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
              <p className="text-sm text-grey-40 dark:text-grey-80">
                {t(
                  "trialRequests.pageDisabledHint",
                  "Requests and messages arrive here once your public page is live. Set it up under Website.",
                )}{" "}
                <Link
                  to={ROUTES.WEBSITE}
                  className="text-primary-40 hover:underline dark:text-primary-90"
                >
                  {t("trialRequests.pageDisabledLink", "Open Website settings")}
                </Link>
              </p>
            </div>
          )}

          <h2 className="mb-4 text-xl font-bold text-grey-20 dark:text-white">
            {t("trialRequests.listTitle", "Trial requests")}
          </h2>
          <div className="mb-4 flex flex-wrap gap-2">
            {FILTERS.map((value) => (
              <button key={value} type="button" onClick={() => setFilter(value)} className={tabClass(filter === value)}>
                {t(`trialRequests.filter.${value}`, value)}
              </button>
            ))}
          </div>
          <TrialRequestsList />

          <h2 className="mt-10 mb-4 text-xl font-bold text-grey-20 dark:text-white">
            {t("contactMessages.title", "Enquiries")}
          </h2>
          <ContactMessagesList />

          <DeletionRequestsList />

          <h2 className="mt-10 mb-4 text-xl font-bold text-grey-20 dark:text-white">
            {t("announcements.title", "Announcements")}
          </h2>
          <AnnouncementsSection />
        </div>
      </div>
    </section>
  );
};

export default TrialRequestsPage;
